<template>
    <div class="container-fluid">
        <!-- Toast Container -->
        <div class="toast-container">
            <o-toast
                v-for="toast in toasts"
                :key="toast.id"
                :type="toast.type"
                :title="toast.title"
                :message="toast.message"
                :duration="5000"
                @close="removeToast(toast.id)"
            />
        </div>

        <div class="d-flex justify-content-center spinner-border text-primary spinner-position form" v-if="transaction.isLoading"></div>
        <div class="spinner" v-if='transaction.isLoading'></div>

        <div class="breadcrumb-wrapper">
            <o-bread-crumb>
                <template v-slot:pageName>
                    Transaction
                </template>
                <template v-slot:pageDetail>
                    {{ transaction.cardTitle }}
                </template>
            </o-bread-crumb>
        </div>

        <div v-if="validation.length" class="error-alert mb-4">
            <i class="fas fa-exclamation-circle error-alert-icon"></i>
            <div>
                <p class="error-alert-title">Please correct the following error(s):</p>
                <ul class="error-list">
                    <li v-for="(validationError, index) in validation" :key="index">{{ validationError }}</li>
                </ul>
            </div>
        </div>

        <!-- Disabled State (Product Module Nonactive) -->
        <div v-if="!isProductModuleActive && !isEditMode && !transaction.isLoading" class="card text-center p-5 mt-4 border-0 shadow-sm" style="border-radius: 12px;">
            <div class="empty-art mb-4"><i class="fas fa-store-slash text-primary" style="font-size: 4rem; opacity: 0.3;"></i></div>
            <h4 class="text-dark fw-bold">POS Membership Disabled</h4>
            <p class="text-muted mb-4">Transaction creation is currently unavailable because both Branch and Merchant Product modules are NONACTIVE.</p>
            <div>
                <button @click="$router.push({ name: 'Transaction' })" class="btn btn-light border px-4 py-2" style="border-radius: 8px;">
                    <i class="fas fa-arrow-left me-1"></i> Back to Transactions
                </button>
            </div>
        </div>

        <!-- Main Layout: 2-Column Split -->
        <div v-if="isProductModuleActive || isEditMode" class="row g-4">
            <!-- Left Panel: Transaction Details -->
            <div class="col-xl-9 col-lg-8">
                <div class="card main-card h-100">
                    <div class="card-header main-header">
                        <div class="d-flex justify-content-between align-items-center">
                            <div class="header-title">
                                <h5 class="mb-0">Transaction Details</h5>
                                <small class="text-muted">Manage products and transaction items</small>
                            </div>

                            <!-- Action Buttons -->
                            <div class="header-actions">
                                <o-button @click="showModal('addProduct')" :disabled="isDisabled" class="btn-custom btn-custom-primary me-2">
                                    <i class="fas fa-plus me-1"></i> Add Product
                                </o-button>
                                <o-button @click="showTaxServiceModal" :disabled="isDisabled" class="btn-custom btn-custom-outline me-2">
                                    <i class="fas fa-percentage me-1"></i> Tax & Service
                                </o-button>
                                <o-button @click="showModal('uploadReceipt')" :disabled="isDisabled" class="btn-custom btn-custom-outline">
                                    <i class="fas fa-receipt me-1"></i> Receipt
                                </o-button>
                            </div>
                        </div>
                    </div>

                    <div class="card-body">
                        <!-- Member Selection with Avatar -->
                        <div class="member-selection-card mb-4">
                            <label class="form-label-premium mb-1">Member Selection</label>
                            <div class="d-flex gap-3 align-items-start">
                                <div class="member-avatar theme-bg mt-4" style="margin-top: 30px !important;" v-if="selectedMemberData">
                                    <img v-if="selectedMemberData.user?.user_profile?.avatar?.image_url && !selectedMemberData.imageError"
                                         :src="selectedMemberData.user.user_profile.avatar.image_url"
                                         @error="selectedMemberData.imageError = true"
                                         class="avatar-img">
                                    <span v-else class="text-white">{{ getInitials(selectedMemberData) }}</span>
                                </div>
                                <div class="flex-grow-1">
                                    <o-member-detail
                                        :members="members"
                                        :member_id="transaction.member_id"
                                        :member_name="formattedMemberName"
                                        :memberTitle="false"
                                        :code="transaction.code"
                                        @update:member_id="handleSelectedMember" />
                                </div>
                            </div>
                        </div>

                        <!-- Validation Messages -->
                        <div v-if="validation.detail" class="error-alert mb-3">
                            <i class="fas fa-exclamation-circle error-alert-icon"></i>
                            <div>
                                <p class="error-alert-title">{{ validation.detail[0] }}</p>
                            </div>
                        </div>
                        <div v-if="validation.member_id" class="error-alert mb-3">
                            <i class="fas fa-exclamation-circle error-alert-icon"></i>
                            <div>
                                <p class="error-alert-title">{{ validation.member_id[0] }}</p>
                            </div>
                        </div>
                        <div v-if="validation.receiptImage" class="error-alert mb-3">
                            <i class="fas fa-exclamation-circle error-alert-icon"></i>
                            <div>
                                <p class="error-alert-title">{{ validation.receiptImage[0] }}</p>
                            </div>
                        </div>

                        <!-- Information Badges (Inline Sync) -->
                        <div class="d-flex flex-wrap gap-2 align-items-center mb-4">
                            <span class="text-muted small fw-bold text-uppercase me-2" style="font-size: 11px;">Information :</span>
                            <span v-if="transaction.is_nett_price !== null" class="source-badge external-nett">EXTERNAL</span>
                            <span v-else class="source-badge internal">MEMBERSHIP APPS</span>

                            <span class="status-badge" :class="transaction.is_expired ? 'expired' : 'active'">
                                {{ transaction.is_expired ? 'EXPIRED' : 'ACTIVE' }}
                            </span>
                        </div>

                        <!-- Clean Premium Custom Table -->
                        <div class="transaction-table-scroll-custom mt-3">
                            <table class="premium-custom-table">
                                <thead>
                                    <tr>
                                        <th class="text-center" width="50">NO</th>
                                        <th width="120">CODE</th>
                                        <th>PRODUCT</th>
                                        <th class="text-center" width="80">QTY</th>
                                        <th class="text-end" width="120">PRICE</th>
                                        <th class="text-end" width="100">DISCOUNT</th>
                                        <th class="text-end" width="150">TOTAL</th>
                                        <th class="text-center" width="100">ACTION</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="(item, index) in transaction.detail" :key="index">
                                        <td class="text-center text-muted small">{{ index + 1 }}</td>
                                        <td>
                                            <div class="code-copy-wrapper">
                                                <code class="p-custom-code-short">{{ item.code }}</code>
                                                <button class="btn-copy-mini" @click.prevent="copyToClipboard(item.code)" title="Copy Code">
                                                    <i class="far fa-copy"></i>
                                                </button>
                                            </div>
                                        </td>
                                        <td class="fw-bold text-dark-soft">{{ item.name }}</td>
                                        <td class="text-center">{{ item.qty }}</td>
                                        <td class="text-end text-nowrap">{{ priceFormat(item.price) }}</td>
                                        <td class="text-end">
                                            <span v-if="item.discount_value > 0" class="badge-disc-custom">
                                                -{{ item.discount_type === 'percentage' ? item.discount_value + '%' : priceFormat(item.discount_value) }}
                                            </span>
                                            <span v-else class="text-muted small">-</span>
                                        </td>
                                        <td class="text-end fw-bold text-dark text-nowrap">{{ priceFormat(item.subtotal) }}</td>
                                        <td class="text-center">
                                            <div class="action-btn-sync">
                                                <button class="btn-sync-action btn-sync-edit" @click.prevent="editModal(index)" :disabled="isDisabled" title="Edit Item">
                                                    <i class="fas fa-edit"></i>
                                                </button>
                                                <button class="btn-sync-action btn-sync-delete" v-if="write" @click="confirmDelete(item)" title="Delete Item">
                                                    <i class="fas fa-trash"></i>
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                    <tr v-if="!transaction.detail.length">
                                        <td colspan="8" class="empty-placeholder-custom text-center">
                                            <i class="fas fa-shopping-basket fa-2x mb-2 text-muted opacity-25"></i>
                                            <p class="mb-0 small text-muted">No products added yet</p>
                                        </td>
                                    </tr>
                                </tbody>
                                <tfoot v-if="transaction.detail.length">
                                    <tr class="footer-total-row-custom">
                                        <td colspan="7" class="text-end footer-label-custom">
                                            TOTAL PRODUCTS <span class="footer-value-custom ms-3">{{ priceFormat(calculateTotal()) }}</span>
                                        </td>
                                    </tr>
                                </tfoot>
                            </table>
                        </div>

                        <!-- VOUCHER APPLY -->
                        <div v-if="transaction.member_id && !isEditMode" class="voucher-horizontal-section mt-4 mb-4 p-4 bg-white border rounded-3">
                            <div class="d-flex align-items-center mb-3">
                                <i class="fas fa-tags primary-text me-2 fs-5"></i>
                                <h6 class="mb-0 fw-bold">Apply Voucher</h6>
                            </div>

                            <div v-if="isLoadingVouchers" class="text-center py-4">
                                <i class="fas fa-spinner fa-spin text-primary fs-4"></i>
                                <p class="small text-muted mt-2 mb-0">Loading available vouchers...</p>
                            </div>

                            <div v-else-if="groupedAvailableVouchers.length > 0" class="voucher-horizontal-container">
                                <div class="voucher-horizontal-list">
                                    <div class="voucher-horiz-card" 
                                         v-for="g in groupedAvailableVouchers" :key="g.member_voucher_ids[0]"
                                         :class="{'selected': g.member_voucher_ids.includes(selectedVoucherId), 'disabled': isDisabled || g.is_disabled}"
                                         @click="if(!isDisabled && !g.is_disabled) { selectedVoucherId = (g.member_voucher_ids.includes(selectedVoucherId) ? null : g.member_voucher_ids[0]); recalculatePreview(); }">
                                        
                                        <div class="voucher-count-badge" v-if="g.count > 1">{{ g.count }}x</div>

                                        <div class="voucher-horiz-header">
                                            <span class="badge-best-deal" v-if="!g.is_disabled && ((g.discount_type === 'percentage' && g.discount_value >= 40) || (g.discount_type !== 'percentage' && g.discount_value >= 100000))">BEST DEAL</span>
                                            <div class="discount-huge">
                                                {{ g.discount_type === 'percentage' ? g.discount_value + '%' : priceFormat(g.discount_value) }} <span class="off-text">OFF</span>
                                            </div>
                                        </div>
                                        <div class="voucher-horiz-body">
                                            <div class="v-title" :title="g.name">
                                                {{ g.name }}
                                            </div>
                                            <div class="v-desc" :class="getExpirationDisplay(g.expired_at).class">
                                                <i class="far fa-clock me-1" v-if="getExpirationDisplay(g.expired_at).class"></i>
                                                {{ getExpirationDisplay(g.expired_at).text }}
                                            </div>
                                            <div class="v-desc text-dark-soft mt-1" v-if="g.is_disabled">
                                                <i class="fas fa-exclamation-circle me-1"></i> {{ g.disabled_reason }}
                                            </div>
                                        </div>
                                        <div class="voucher-horiz-footer">
                                            <div class="check-circle" v-if="!g.is_disabled && g.member_voucher_ids.includes(selectedVoucherId)">
                                                <i class="fas fa-check"></i>
                                            </div>
                                            <div class="uncheck-circle" v-else-if="!g.is_disabled"></div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            
                            <div class="fintech-empty-voucher text-center" v-else>
                                <i class="fas fa-box-open fa-2x mb-2 text-muted opacity-25"></i>
                                <p class="mb-0 small text-muted">No eligible vouchers available.</p>
                            </div>
                            
                            <div class="applied-alert mt-3" v-if="selectedVoucherId">
                                <div class="d-flex justify-content-between align-items-center">
                                    <div class="text-success small fw-bold">
                                        <i class="fas fa-check-circle me-1"></i> Voucher applied successfully
                                    </div>
                                    <button class="btn btn-link text-danger p-0 text-decoration-none" @click="selectedVoucherId = null; recalculatePreview();" title="Remove Voucher">
                                        <i class="fas fa-trash-alt fs-6"></i>
                                    </button>
                                </div>
                            </div>
                        </div>

                    </div>
                </div>
            </div>

            <!-- Right Panel: Combined Summary Card -->
            <div class="col-xl-3 col-lg-4">
                <div class="card summary-card sticky-top" style="top: 24px;">
                    <div class="card-header summary-header">
                        <h6 class="mb-0">Action Summary</h6>
                    </div>
                    <div class="card-body p-0">
                        <!-- Fee Calculations -->
                        <div class="summary-section p-3">
                            <div class="summary-row mb-3">
                                <span class="summary-label">Subtotal</span>
                                <span class="summary-value">{{ priceFormat(transaction.before_tax_service) }}</span>
                            </div>

                            <div v-if="transaction.tax_charge || transaction.service_charge" class="tax-detail-group mb-3">
                                <div class="summary-row">
                                    <span class="summary-label">Tax & Service</span>
                                    <span class="summary-value">{{ priceFormat((transaction.tax_value || 0) + (transaction.service_value || 0)) }}</span>
                                </div>
                                <div class="tax-sublist ps-3 border-start ms-2 mt-2">
                                    <div class="summary-row-sm mb-1">
                                        <span class="label-sm">Tax ({{ transaction.tax_charge }}%)</span>
                                        <span class="value-sm">{{ priceFormat(transaction.tax_value) }}</span>
                                    </div>
                                    <div class="summary-row-sm">
                                        <span class="label-sm">Service ({{ transaction.service_charge }}%)</span>
                                        <span class="value-sm">{{ priceFormat(transaction.service_value) }}</span>
                                    </div>
                                </div>
                            </div>

                            <div v-if="transaction.voucher_discount > 0" class="summary-row text-success mb-3 align-items-center">
                                <span class="summary-label">Voucher Discount</span>
                                <span class="summary-value px-2 py-1 rounded bg-success text-white fw-bold shadow-sm" style="font-size: 14px;">-{{ priceFormat(transaction.voucher_discount) }}</span>
                            </div>
                            <!-- detail text below if voucher_detail is present -->
                            <div v-if="transaction.voucher_detail" class="text-muted small ms-2 mb-3 mt-n2" style="font-size: 11px;">
                                <i class="fas fa-ticket-alt me-1"></i><strong>{{ transaction.voucher_detail.voucher?.name }}</strong> Applied
                            </div>

                            <div class="summary-row-grand mt-4 pt-4 border-top">
                                <span class="label-grand">Grand Total</span>
                                <span class="value-grand">{{ priceFormat(transaction.after_tax_service) }}</span>
                            </div>

                            <!-- Price Type Toggle -->
                            <div class="price-toggle-box mt-4" v-if="taxServiceLoaded && transaction.is_nett_price === null">
                                <div class="d-flex justify-content-between align-items-center">
                                    <span class="small fw-bold text-muted text-uppercase">Price Type</span>
                                    <div class="toggle-control">
                                        <span class="toggle-text me-2" :class="{active: !includeTaxAndService}">Exclude</span>
                                        <label class="toggle-switch-sm">
                                            <input type="checkbox" v-model="includeTaxAndService" @change="recalculatePreview">
                                            <span class="toggle-slider-sm"></span>
                                        </label>
                                        <span class="toggle-text ms-2" :class="{active: includeTaxAndService}">Include</span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <!-- Point Calculation (Integrated) -->
                        <div class="summary-section point-generation-card">
                            <div class="d-flex justify-content-between align-items-center mb-3">
                                <span class="point-heading">Point Generated</span>
                                <span class="badge-point-base mt-0" :class="backendPointBase">
                                    {{ backendPointBase === 'before_tax' ? 'Before Tax' : 'After Tax' }}
                                </span>
                            </div>

                            <div class="point-score-board mb-3">
                                <div class="point-icon-large">
                                    <i class="fas fa-coins"></i>
                                </div>
                                <div class="point-score-content">
                                    <span class="point-count-large" v-if="!isCalculatingPoint">{{ isEditExternal ? (transaction.point || 0) : pointEstimate }}</span>
                                    <i v-else class="fas fa-spinner fa-spin text-muted"></i>
                                    <span class="point-unit-large">Points</span>
                                </div>
                            </div>

                            <div class="point-notice-soft">
                                <i class="fas fa-info-circle"></i>
                                <span>Calculated from {{ backendPointBase === 'before_tax' ? (includeTaxAndService ? 'subtotal excluding tax' : 'subtotal before tax') : (includeTaxAndService ? 'subtotal including tax' : 'grand total including tax') }}</span>
                            </div>
                        </div>

                        <!-- Note Field -->
                        <div class="summary-section p-3 border-top">
                            <label class="form-label-premium mb-1">Note (Required)</label>
                            <textarea
                                v-model="transaction.note"
                                class="form-control"
                                :class="{ 'is-invalid': validation.note }"
                                :disabled="isDisabled"
                                rows="4"
                                maxlength="1000"
                                placeholder="Write a clean note..."
                                style="resize: none;"
                            ></textarea>
                            <div v-if="validation.note" class="invalid-feedback d-block mt-1" style="font-size: 12px;">
                                {{ validation.note[0] || validation.note }}
                            </div>
                            <div class="d-flex justify-content-end mt-1">
                                <small class="text-muted" style="font-size: 10px;">{{ (transaction.note || '').length }} / 1000</small>
                            </div>
                        </div>

                        <!-- Main Actions -->
                        <div class="summary-section p-3">
                            <div class="d-flex flex-column gap-3">
                                <o-form @submit.prevent="change()" autocomplete="off">
                                    <o-button type="submit" :disabled="isDisabled || transaction.isLoading" class="btn-main-action w-100">
                                        <span v-if="transaction.isLoading"><i class="fas fa-spinner fa-spin me-2"></i>Saving...</span>
                                        <span v-else><i class="fas fa-save me-2"></i>Save Transaction</span>
                                    </o-button>
                                </o-form>
                                <o-button @click="cancel" class="btn-secondary-action w-100">
                                    Cancel
                                </o-button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Modals -->
        <transaction-detail
            v-if="isModalVisible && modalType === 'addProduct'"
            @parentAddItem="saveDetail"
            @parentCloseModal="closeModal"
            :editedItem="editedItem"
            @deleteItem="destroyModal"
            :merchantid="transaction.merchant_id"
        ></transaction-detail>

        <transaction-discount
            v-if="isModalVisible && modalType === 'addDiscount'"
            @parentAddItem="saveDetail"
            @parentCloseModal="closeModal"
            :editedItem="editedItem"
            @deleteItem="destroyModal"
            :merchantid="transaction.merchant_id"
        ></transaction-discount>

        <upload-receipt
            v-if="isModalVisible && modalType === 'uploadReceipt'"
            @saveDetail="saveReceipt"
            :editedItem="editedItem"
            @closeModal="closeModal"
            @selectImage="setImage"
        ></upload-receipt>

        <tax-service-detail
            v-if="isModalVisible && modalType === 'editTaxService'"
            @parentAddItem="saveDetail"
            @parentCloseModal="closeModal"
            :editedItem="editedItem"
            @deleteItem="destroyModal"
        />

        <o-warning-box
            v-if="isPendingTransactionWarningVisible"
            :title="'Error Transaction'"
            :message="error"
            :action-text="actionText"
            @action="actionHandler"
            @cancel="closePendingTransactionWarning()"
            :hide-action="false"
        />
    </div>

     <o-warning-box
            v-if="isDeleteConfirmVisible"
            title="Delete Item"
            :item-name="itemToDelete?.name || 'this item'"
            warning-message="This action cannot be undone."
            confirm-text="Yes, Delete"
            cancel-text="Cancel"
            :is-loading="transaction.isLoading"
            @confirm="deleteItem"
            @cancel="closeDeleteConfirm"
        />
</template>

<script>
import { OForm, OBreadCrumb, OButton, OMemberDetail, OWarningBox, OToast } from '@/components/OmnihControl';
import TransactionDetail from './transaction/TransactionDetail.vue';
import UploadReceipt from './transaction/UploadReceipt.vue';
import TransactionDiscount from './transaction/TransactionDiscount.vue';
import TaxServiceDetail from './transaction/TaxServiceDetail.vue';
import dayjs from 'dayjs';
import store from '@/store'
import {mapState} from 'vuex'

export default {
    name: 'AddTransaction',
    components: {
        OForm,
        OButton,
        OBreadCrumb,
        TransactionDetail,
        UploadReceipt,
        TransactionDiscount,
        TaxServiceDetail,
        OMemberDetail,
        OWarningBox,
        OToast,
    },
    data: function() {
        return{
            items: [],
            transaction: {
                id: '',
                code: '',
                member_id: '',
                merchant_id: '',
                point: '',
                member_name: '',
                is_pending: 0,
                detail: [],
                isLoading: false,
                cardTitle: 'Create transaction',
                dataReady: false,
                receiptImage: '',
                tax_charge: 0,
                service_charge: 0,
                tax_value: 0,
                service_value: 0,
                before_tax_service: 0,
                after_tax_service: 0,
                voucher_discount: 0,
                voucher_detail: null,
                is_nett_price: null,
                point_estimated: 0,
                is_expired: false,
                note: '',
            },
            options: [],
            validation: [],
            fields: ([
                'No',
                'Code',
                'Name Product',
                'Qty',
                'Item Price',
                'Discount',
                'Total',
                'Action'
            ]),
            currentRouteName: '',
            isModalVisible: false,
            modalType: '',
            editedItem: {},
            merchants: [],
            members: [],
            selectedBusinessProfile: {
                corporate_id: null,
                branch_id: null,
                merchant_id: null
            },
            error: '',
            includeTaxAndService: false,
            backendPointBase: 'before_tax',
            taxServiceAmount: null,
            taxService: null,
            pointPlans: null,
            pointSystemMode: 'DECENTRALIZE',
            taxServiceLoaded: false,
            isDisabled: false,
            originalPayableAmount: 0,
            isPendingTransactionWarningVisible: false,
            actionText: '',
            actionHandler: null,
            toasts: [],
            pointEstimate: 0,
            isCalculatingPoint: false,
            pointCalculationDebounce: null,
            isDeleteConfirmVisible: false,
            itemToDelete: null,
            write: true,
            availableVouchers: [],
            selectedVoucherId: null,
            isLoadingVouchers: false,
        }
    },
    async mounted() {
        this.transaction.isLoading = true;
        this.currentRouteName = this.$route.name;

        try {
            await this.loadBusinessProfile();
            await this.loadFeatureFlag();
            await this.loadPointPlans();
            await this.loadTaxServiceAmount();
            if (this.$route.params.id) {
                await this.loadState(this.$route.params.id);
            } else if (this.$route.query.member_id) {
                this.transaction.member_id = this.$route.query.member_id;
                this.transaction.member_name = this.$route.query.member_name || '';
                
                if (!this.members.find(m => m.value == this.transaction.member_id)) {
                    this.members.push({
                        value: this.transaction.member_id,
                        text: this.transaction.member_name
                    });
                }

                // Fetch full member data from API
                import('@/store').then(async ({ default: store }) => {
                    const storeMembers = store.state.Member.members || [];
                    if (!storeMembers.find(m => m.id == this.transaction.member_id)) {
                        // Insert dummy member first for instant UI response
                        store.commit('Member/add_single_member', { 
                            member: { 
                                id: this.transaction.member_id, 
                                title: '', 
                                first_name: this.transaction.member_name, 
                                last_name: '', 
                                user: {} 
                            } 
                        });
                        
                        try {
                            const res = await store.dispatch('Member/read_single', this.transaction.member_id);
                            if (res) {
                                // Overwrite dummy member with the real data
                                store.commit('Member/add_single_member', { member: res });
                                
                                const title = res.title ? res.title + ' ' : '';
                                const firstName = res.first_name ? res.first_name : '';
                                const lastName = res.last_name ? ' ' + res.last_name : '';
                                let fullName = (title + firstName + lastName).trim();
                                if (res.user && res.user.email) {
                                    fullName += ' (' + res.user.email + ')';
                                }
                                
                                const idx = this.members.findIndex(m => m.value == res.id);
                                if (idx !== -1) {
                                    this.members[idx].text = fullName;
                                }
                                this.transaction.member_name = fullName;
                            }
                        } catch (err) {
                            console.error('Failed to fetch full member data', err);
                        }
                    }
                });
                
                this.loadAvailableVouchers();
            }
            await this.recalculatePreview();
        } catch (error) {
            // Error loading data on mount
        } finally {
            this.transaction.isLoading = false;
        }
    },
    computed: {
        ...mapState({
            products: state => state.Product.products || []
        }),
        isEditMode() {
            return !!this.$route.params.id;
        },
        isProductModuleActive() {
            const branchProductFlag = store.getters['FeatureFlag/getBranchProductManagementFlag'];
            const merchantProductFlag = store.getters['FeatureFlag/getMerchantProductFlag'];
            return branchProductFlag === 'ACTIVE' || merchantProductFlag === 'ACTIVE';
        },
        groupedAvailableVouchers() {
            const groups = {};
            this.availableVouchers.forEach(v => {
                const key = v.voucher_id || (v.name + '_' + v.discount_value);
                if (!groups[key]) {
                    groups[key] = {
                        ...v,
                        count: 1,
                        member_voucher_ids: [v.member_voucher_id]
                    };
                } else {
                    groups[key].count++;
                    groups[key].member_voucher_ids.push(v.member_voucher_id);
                }
            });
            
            let vouchers = Object.values(groups);
            
            vouchers.sort((a, b) => {
                // Priority 1: Enabled vouchers
                if (a.is_disabled !== b.is_disabled) {
                    return a.is_disabled ? 1 : -1;
                }
                
                // Priority 2: Best Deal
                const isBestDealA = (a.discount_type === 'percentage' && a.discount_value >= 40) || (a.discount_type !== 'percentage' && a.discount_value >= 100000);
                const isBestDealB = (b.discount_type === 'percentage' && b.discount_value >= 40) || (b.discount_type !== 'percentage' && b.discount_value >= 100000);
                
                if (isBestDealA && !isBestDealB) return -1;
                if (!isBestDealA && isBestDealB) return 1;
                
                // Priority 3: Highest discount value
                return b.discount_value - a.discount_value;
            });
            
            return vouchers;
        },
        priceFormat() {
            return (subtotal) => {
                if (typeof subtotal === 'string') {
                    const numericValue = parseFloat(subtotal.replace(/[^0-9.-]+/g, ""));
                    if (!isNaN(numericValue)) {
                        subtotal = numericValue;
                    } else {
                        return '0';
                    }
                }
                if (typeof subtotal !== 'number') return '0';
                const roundedValue = Math.round(subtotal);
                return roundedValue.toString().replace(/[^0-9]/g,'').replace(/(\d)(?=(\d{3})+(?!\d))/g,'$1.');
            }
        },
        /**
         * pointCalculationBase — resolved by backend via PointBaseResolverService
         * Read from `backendPointBase` data property (set by recalculatePreview API response)
         */
        pointCalculationBase() {
            return this.backendPointBase;
        },
        isPointGenerated() {
            if (this.transaction.cardTitle === 'Edit transaction') {
                return this.transaction.after_tax_service === this.originalPayableAmount;
            }
            return false;
        },
        isEditExternal() {
            return this.transaction.cardTitle === 'Edit transaction' && this.transaction.is_nett_price !== null;
        },
        selectedMemberData() {
            if (!this.transaction.member_id) return null;
            const fromStore = store.state.Member.members.find(m => m.id === this.transaction.member_id);
            if (fromStore) return fromStore;

            if (this.transaction.member && this.transaction.member.id === this.transaction.member_id) {
                return this.transaction.member;
            }
            return null;
        },
        formattedMemberName() {
            const name = this.transaction.member_name || '';
            if (name.length > 25) {
                return name.substring(0, 25) + '..';
            }
            return name;
        }
    },
    watch: {
        'transaction.detail': {
            handler() {
                this.recalculatePreview();
                this.loadAvailableVouchers();
            },
            deep: true
        },
        'transaction.note'(val) {
            if (val && val.trim() && this.validation && this.validation.note) {
                this.validation = [];
            }
        },
    },
    methods:{
        showToast(type, title, message) {
            const toast = { id: Date.now(), type, title, message };
            this.toasts.push(toast);
        },
        removeToast(id) {
            this.toasts = this.toasts.filter(t => t.id !== id);
        },
        getExpirationDisplay(expiredAt) {
            if (!expiredAt) return { text: 'No Expiration', class: '' };

            let dateStr = expiredAt;
            if (dateStr.indexOf('T') === -1 && dateStr.indexOf('Z') === -1 && dateStr.length === 19) {
                dateStr = dateStr.replace(' ', 'T');
            }
            const expiredDate = new Date(dateStr);
            const now = new Date();
            
            const diffMs = expiredDate - now;
            
            if (diffMs <= 0) {
                return { text: 'Expired', class: 'text-danger fw-bold' };
            }
            
            const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
            const diffHours = Math.floor((diffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
            const diffMinutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));

            if (diffDays >= 7) {
                const day = expiredDate.getDate().toString().padStart(2, '0');
                const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
                const monthName = months[expiredDate.getMonth()];
                const year = expiredDate.getFullYear();
                return { text: `Valid until ${day} ${monthName} ${year}`, class: 'fw-bold' };
            } else if (diffDays >= 1) {
                return { text: `Expiring in ${diffDays} days ${diffHours} hours`, class: 'text-warning fw-bold' };
            } else {
                return { text: `Expiring in ${diffHours} hours ${diffMinutes} minutes`, class: 'text-danger fw-bold' };
            }
        },
        async loadProducts() {
            await store.dispatch('Product/all', this.transaction.merchant_id);
        },
        /**
         * Recalculate transaction preview from backend — single source of truth.
         * Backend calculates tax breakdown + point estimate from TaxService config.
         * FE sends overrides (tax%, service%, include/exclude) when user edits them.
         */
        async recalculatePreview() {
            // Do not recalculate for external transactions (Booking Engine / POS).
            if (this.transaction.is_nett_price !== null) {
                return;
            }

            if (!this.transaction.merchant_id || !this.transaction.detail.length) {
                this.pointEstimate = 0;
                return;
            }
            // Debounce to avoid too many API calls
            if (this.pointCalculationDebounce) {
                clearTimeout(this.pointCalculationDebounce);
            }
            this.pointCalculationDebounce = setTimeout(async () => {
                this.isCalculatingPoint = true;
                try {
                    // Send essential fields + optional overrides from Edit Tax modal & toggle
                    const payload = {
                        merchant_id: this.transaction.merchant_id,
                        detail: this.transaction.detail.map(item => ({ subtotal: Number(item.subtotal) || 0 })),
                    };

                    // Send overrides if user has customized tax/service via Edit Tax modal
                    if (this.transaction.tax_charge) payload.tax_charge = Number(this.transaction.tax_charge);
                    if (this.transaction.service_charge) payload.service_charge = Number(this.transaction.service_charge);
                    // Send include/exclude toggle state
                    payload.include_tax = this.includeTaxAndService;
                    if (this.selectedVoucherId) payload.member_voucher_id = this.selectedVoucherId;
                    if (this.isEditMode && this.transaction.voucher_discount) payload.voucher_discount = this.transaction.voucher_discount;

                    const result = await store.dispatch('Transaction/calculateTransactionPreview', { data: payload });

                    // === Update tax breakdown from backend ===
                    this.transaction.tax_charge = result.tax_charge || 0;
                    this.transaction.service_charge = result.service_charge || 0;
                    this.transaction.tax_value = result.tax_value || 0;
                    this.transaction.service_value = result.service_value || 0;
                    this.transaction.before_tax_service = result.before_tax_service || 0;
                    this.transaction.after_tax_service = result.after_tax_service || 0;
                    if (!this.isEditMode) {
                        this.transaction.voucher_discount = result.discount || 0;
                    }

                    // === Update point estimate from backend ===
                    this.pointEstimate = result.point_estimated || 0;
                    this.backendPointBase = result.point_calculation_base || 'before_tax';
                } catch (error) {
                    // If API fails, keep existing values
                    this.pointEstimate = 0;
                } finally {
                    this.isCalculatingPoint = false;
                }
            }, 300);
        },
        async loadState(id) {
            this.transaction.isLoading = true;
            try {
                const data = await store.dispatch('Transaction/read_single', id);
                this.transaction.id = data.id;
                this.transaction.code = data.code;
                this.transaction.member_id = data.member_id;
                this.transaction.merchant_id = data.merchant_id;
                this.transaction.point = data.point;
                this.transaction.member_name = data.member_name;
                this.transaction.member = data.member;

                if (data.member_id && !this.members.find(m => m.value == data.member_id)) {
                    this.members.push({
                        value: data.member_id,
                        text: data.member_name || (data.member ? `${data.member.title || ''} ${data.member.first_name || ''} ${data.member.last_name || ''}`.trim() : '')
                    });
                }

                if (data.member_id) {
                    import('@/store').then(async ({ default: store }) => {
                        const storeMembers = store.state.Member.members || [];
                        if (!storeMembers.find(m => m.id == data.member_id)) {
                            if (data.member) {
                                store.commit('Member/add_single_member', { member: data.member });
                            }
                            try {
                                const fullMember = await store.dispatch('Member/read_single', data.member_id);
                                if (fullMember) {
                                    store.commit('Member/add_single_member', { member: fullMember });
                                }
                            } catch (err) {
                                console.error('Failed to load full member data for avatar in edit mode', err);
                            }
                        }
                    });
                }

                this.transaction.tax_charge = data.tax_charge;
                this.transaction.service_charge = data.service_charge;
                this.transaction.tax_value = data.tax_value;
                this.transaction.service_value = data.service_value;
                this.transaction.before_tax_service = data.before_tax_service;
                this.transaction.after_tax_service = data.after_tax_service;
                this.transaction.voucher_discount = data.voucher_discount || 0;
                this.transaction.voucher_detail = data.voucher_detail || null;
                this.transaction.is_nett_price = data.is_nett_price;
                this.transaction.point_estimated = data.point_estimated;
                this.originalPayableAmount = data.after_tax_service;
                this.pointEstimate = data.point || data.point_estimated || 0;
                this.transaction.is_expired = data.is_expired || false;
                this.transaction.note = data.note || '';
                this.isDisabled = this.transaction.is_nett_price !== null || this.transaction.is_expired || !this.isProductModuleActive;
                this.write = !this.isDisabled;

                if (data.is_nett_price !== null) {
                    this.includeTaxAndService = data.is_nett_price === 'nett';
                    this.taxServiceLoaded = true;
                    this.taxService = { tax: data.tax_charge, service: data.service_charge };
                } else {
                    // For internal transactions, infer from math if calculate_tax_service is 0 (legacy data)
                    const detailSubtotalSum = data.transaction_detail.reduce((sum, item) => sum + (parseFloat(item.subtotal) || 0), 0);
                    const effectiveSubtotal = detailSubtotalSum - (parseFloat(data.voucher_discount) || 0);

                    if (data.calculate_tax_service === 1) {
                        this.includeTaxAndService = true;
                    } else if (Math.abs(data.after_tax_service - effectiveSubtotal) < 1 && data.after_tax_service > 0 && data.tax_charge > 0) {
                        this.includeTaxAndService = true;
                    } else {
                        this.includeTaxAndService = false;
                    }
                    
                    if (data.tax_charge === null && data.service_charge === null) {
                        this.taxService = null;
                        this.taxServiceLoaded = false;
                    } else {
                        this.taxService = { tax: data.tax_charge, service: data.service_charge };
                    }
                }

                await this.loadProducts();
                this.transaction.detail = data.transaction_detail.map(item => {
                    if (item.discount_type && item.discount_type == "percentage") {
                        const disc = item.discount_value/100;
                        const subTot = parseFloat(item.subtotal);
                        const discMinus = 1 - disc;
                        item.price = Math.round((subTot / Number(discMinus.toFixed(2))) / item.qty);
                    }
                    if (item.discount_type && item.discount_type == "amount") {
                        item.price = Math.round((parseFloat(item.subtotal) + item.discount_value)/item.qty);
                    }
                    if (!item.discount_type) {
                        item.price = Math.round(parseFloat(item.subtotal)/item.qty);
                    }
                    return item;
                });

                this.transaction.receiptImage = data?.receipt?.image_url;
                this.transaction.cardTitle = 'Edit transaction';
            } catch (error) {
                // Error loading state
            } finally {
                this.transaction.isLoading = false;
            }
        },
        async loadBusinessProfile() {
            const businessProfile = await store.dispatch('Auth/getBusinessProfile');
            this.selectedBusinessProfile.corporate_id = businessProfile.corporate_id;
            this.selectedBusinessProfile.branch_id = businessProfile.branch_id;
            this.selectedBusinessProfile.merchant_id = businessProfile.merchant_id;
            this.transaction.merchant_id = businessProfile.merchant_id;
            if (businessProfile.merchant_id == null) {
                this.loadMerchants();
            }
            if (businessProfile.corporate_id !== null) {
                this.loadMembers();
            }
            await this.loadTaxService();
            await this.loadTaxServiceAmount();
        },
        loadMerchants(){
            const filter = { branch_id: this.selectedBusinessProfile.branch_id }
            store.commit('Merchant/add_filter', {filter});
            store.dispatch('Merchant/read').then(() => {
                this.merchants = [...store.state.Merchant.merchants.map((merch)=>({'value':merch.id,'text':merch.name}))]
            })
        },
        loadMembers(){
            const filter = { corporate_id: this.selectedBusinessProfile.corporate_id }
            store.commit('Member/add_filter', {filter});
            store.dispatch('Member/read').then(() => {
                this.members = [...store.state.Member.members.map((member) => ({
                    'value': member.id,
                    'text': `${member.title} ${member.first_name} ${member.last_name}`
                }))]
            })
        },
        async loadTaxServiceAmount() {
            const { corporate_id } = this.selectedBusinessProfile;
            if (!corporate_id) return;
            try {
                const response = await store.dispatch('TaxService/read_single_apps', corporate_id);
                if (response && response.type === 'after') {
                    this.taxServiceAmount = response;
                    this.backendPointBase = 'after_tax';
                } else if (response && response.type === 'before') {
                    this.taxServiceAmount = response;
                    this.backendPointBase = 'before_tax';
                } else {
                    this.taxServiceAmount = { ...response, type: 'before' };
                    this.backendPointBase = 'before_tax';
                }
            } catch (error) {
                this.taxServiceAmount = null;
                this.backendPointBase = 'before_tax';
            }
        },
        async loadTaxService() {
            const { merchant_id, branch_id, corporate_id } = this.selectedBusinessProfile;
            let idToUse = merchant_id || branch_id || corporate_id;
            try {
                if (idToUse) {
                    const response = await store.dispatch('TaxService/read_single_transaction', idToUse);
                    if (response) {
                        this.taxService = response;
                        this.taxServiceLoaded = true;
                        this.includeTaxAndService = this.taxService.tax_service_type === 'include';
                    } else {
                        this.taxService = null;
                        this.taxServiceLoaded = false;
                    }
                } else {
                    this.taxServiceLoaded = false;
                }
            } catch (error) {
                this.taxServiceLoaded = false;
            }
        },
        async loadFeatureFlag() {
            const { corporate_id } = this.selectedBusinessProfile;

            if (!corporate_id) return;

            try {
                await store.dispatch('FeatureFlag/fetchFeatureFlag', corporate_id);
                this.pointSystemMode = store.getters['FeatureFlag/getPointControlFlag'];
            } catch (error) {
                this.pointSystemMode = 'DECENTRALIZE';
            }
        },
        async loadPointPlans() {
            const {
                merchant_id,
                branch_id,
                corporate_id
            } = this.selectedBusinessProfile;

            let idToUse = null;

            if (this.pointSystemMode === 'CENTRALIZE' || this.pointSystemMode === 'NONACTIVE') {
                // CENTRAL Point Plan
                idToUse = corporate_id;
            } else {
                // DECENTRALIZE Point Plan
                if (merchant_id) {
                    idToUse = merchant_id;
                } else if (branch_id) {
                    idToUse = branch_id;
                } else if (corporate_id) {
                    idToUse = corporate_id;
                }
            }

            try {
                const response = await store.dispatch('Pointplan/read_single_plan', idToUse);
                this.pointPlans = response;
            } catch (error) {
                // Failed to load point plans
            }
        },
        needsTaxServiceUpdate(item) {
            if (!this.taxService && !this.transaction.tax_charge && !this.transaction.service_charge) return false;
            const taxPercent = (this.transaction.tax_charge && this.transaction.tax_charge !== 0)
                ? parseFloat(this.transaction.tax_charge)
                : (this.taxService?.tax ? parseFloat(this.taxService.tax) : 0);
            const servicePercent = (this.transaction.service_charge && this.transaction.service_charge !== 0)
                ? parseFloat(this.transaction.service_charge)
                : (this.taxService?.service ? parseFloat(this.taxService.service) : 0);
            const allValuesNull = (item.tax_value == null || item.tax_value === '') &&
                (item.service_value == null || item.service_value === '') &&
                (item.tax_type == null || item.tax_type === '') &&
                (item.service_type == null || item.service_type === '');
            if (allValuesNull) return false;
            if (item.tax_type !== 'percentage' || item.service_type !== 'percentage') return true;
            return parseFloat(item.tax_value) !== taxPercent || parseFloat(item.service_value) !== servicePercent;
        },
        applyTaxServiceToDetailItem(index) {
            const item = this.transaction.detail[index];
            if (!item || !this.taxService) return;
            const base = parseFloat(item.before_tax_service || item.originalSubtotal || 0);
            this.transaction.detail[index] = {
                ...item,
                tax_type: null,
                service_type: null,
                tax_value: null,
                service_value: null,
                subtotal: base,
            };
        },
        setImage(imageData) {
            this.editedItem.image = imageData;
        },
        showModal(type, editedItem = null) {
            this.modalType = type;
            this.isModalVisible = true;
            if (type === 'uploadReceipt') {
                this.editedItem = { image: this.transaction.receiptImage || null };
            } else if (type === 'addProduct') {
                this.editedItem = editedItem || {
                    member_name: this.transaction.member_name || '',
                    member_id: this.transaction.member_id || '',
                };
                this.isPendingTransactionWarningVisible = false;
            } else if (type === 'addDiscount') {
                this.editedItem = editedItem || {
                    member_name: this.transaction.member_name || '',
                    member_id: this.transaction.member_id || '',
                };
            } else if (type === 'selectMember') {
                this.isModalVisible = false;
                this.modalType = '';
                this.isPendingTransactionWarningVisible = false;
                this.$nextTick(() => {
                    const selectMemberButton = document.getElementById("select-member-button");
                    if (selectMemberButton) selectMemberButton.click();
                });
            }
        },
        closeModal: function() {
            this.isModalVisible = false;
            this.modalType = '';
        },
        handleSelectedMember(member_id) {
            this.transaction.member_id = member_id;
            const selectedMember = this.members.find(member => member.value === member_id);
            this.transaction.member_name = selectedMember ? selectedMember.text : '';
            this.selectedVoucherId = null;
            this.loadAvailableVouchers();
        },
        async loadAvailableVouchers() {
            if (!this.transaction.member_id || !this.transaction.merchant_id || !this.transaction.detail.length || this.isEditMode) {
                this.availableVouchers = [];
                return;
            }
            this.isLoadingVouchers = true;
            try {
                const subtotal = this.calculateTotal();
                const response = await store.dispatch('Transaction/getAvailableVouchers', {
                    memberId: this.transaction.member_id,
                    params: {
                        custom: {
                            merchant_id: this.transaction.merchant_id,
                            transaction_amount: subtotal,
                            voucher_type: 'credit_discount'
                        }
                    }
                });
                if (response && (response.applicable || response.not_applicable)) {
                    let combined = [];
                    if (response.applicable) {
                        combined = combined.concat(
                            response.applicable
                                .filter(v => this.getExpirationDisplay(v.expired_at).text !== 'Expired')
                                .map(v => ({ ...v, is_disabled: false }))
                        );
                    }
                    if (response.not_applicable) {
                        const disabledVouchers = response.not_applicable
                            .filter(v => 
                                v.reasons && 
                                v.reasons.some(r => r.includes('Minimum transaction')) &&
                                this.getExpirationDisplay(v.expired_at).text !== 'Expired'
                            )
                            .map(v => ({ 
                                ...v, 
                                is_disabled: true,
                                disabled_reason: v.reasons.find(r => r.includes('Minimum transaction'))
                            }));
                        combined = combined.concat(disabledVouchers);
                    }
                    this.availableVouchers = combined;
                    
                    if (this.selectedVoucherId) {
                        const isValid = this.availableVouchers.find(v => v.member_voucher_id === this.selectedVoucherId && !v.is_disabled);
                        if (!isValid) {
                            this.selectedVoucherId = null;
                            this.recalculatePreview();
                        }
                    }
                }
            } catch (error) {
                console.error("Failed to load available vouchers", error);
                this.availableVouchers = [];
                if (this.selectedVoucherId) {
                    this.selectedVoucherId = null;
                    this.recalculatePreview();
                }
            } finally {
                this.isLoadingVouchers = false;
            }
        },
        calculateTotal() {
            let total = 0;
            this.transaction.detail.forEach(item => {
                total += Number(item.subtotal || 0);
            });
            return total;
        },
        saveDetail(dataDariChild) {
            if (this.modalType === 'editTaxService') {
                this.taxService = {
                    tax: Number(dataDariChild.tax) || 0,
                    service: Number(dataDariChild.service) || 0
                };
                this.transaction.tax_charge = this.taxService.tax;
                this.transaction.service_charge = this.taxService.service;
                this.isModalVisible = false;
                this.recalculatePreview();
                return;
            }
            const selectedCode = this.transaction.code;
            const displayCode = selectedCode ? selectedCode : "";
            const tempData = {
                ...dataDariChild,
                code: displayCode,
                originalSubtotal: dataDariChild.originalSubtotal,
                discount_type: dataDariChild.discount_type,
                discount_value: dataDariChild.discount_value,
            };
            if (dataDariChild.index === undefined) {
                this.transaction.detail = [...this.transaction.detail, tempData];
            } else {
                this.transaction.detail[dataDariChild.index] = tempData;
            }
            this.isModalVisible = false;
        },
        validateReceipt() {
            this.validation = [];
        },
        showTaxServiceModal() {
            let edited = { ...(this.taxService || {}) };
            if ('merchant_id' in edited) delete edited.merchant_id;
            this.editedItem = edited;
            this.modalType = 'editTaxService';
            this.isModalVisible = true;
        },
        saveReceipt(dataReceipt) {
            const imageData = dataReceipt.get('image');
            if (imageData) this.transaction.receiptImage = imageData;
            this.isModalVisible = false;
        },
        editModal(index) {
            this.editedItem = { ...this.transaction.detail[index] };
            this.editedItem.transaction = this.transaction;
            this.editedItem.index = index;
            this.showModal('addProduct', this.editedItem);
        },
        editDiscount(index) {
            this.editedItem = { ...this.transaction.detail[index] };
            this.editedItem.transaction = this.transaction;
            this.editedItem.index = index;
            this.showModal('addDiscount', this.editedItem);
        },
        confirmDelete(item) {
            this.itemToDelete = item;
            this.isDeleteConfirmVisible = true;
        },
        deleteItem() {
            if (!this.itemToDelete) return;

            const index = this.transaction.detail.indexOf(this.itemToDelete);
            if (index > -1) {
                this.transaction.detail.splice(index, 1);
            }
            this.closeDeleteConfirm();
        },
        closeDeleteConfirm() {
            this.isDeleteConfirmVisible = false;
            this.itemToDelete = null;
        },
        cleanTransaction(transaction) {
            const seenObjects = new WeakSet();
            const cleanedTransaction = JSON.parse(JSON.stringify(transaction, (key, value) => {
                if (typeof value === 'object' && value !== null) {
                    if (seenObjects.has(value)) return;
                    seenObjects.add(value);
                }
                if (key === 'detail' && typeof value === 'object' && value !== null) {
                    return value.map(item => ({ ...item }));
                }
                return value;
            }));
            return cleanedTransaction;
        },
        transformForCreate(transaction) {
            // Backend recalculates tax from config + overrides — FE sends minimal payload
            const payload = {
                ...transaction,
                point_estimated: this.pointEstimate,
                include_tax: this.includeTaxAndService,
                calculate_tax_service: this.includeTaxAndService ? 1 : 0,
                before_after_tax_service: this.backendPointBase === 'after_tax' ? 1 : 0,
                note: this.transaction.note?.trim() || null,
                member_voucher_id: this.selectedVoucherId,
                voucher_discount: this.transaction.voucher_discount,
                claimed_at: this.selectedVoucherId ? dayjs(new Date()).format('YYYY-MM-DD HH:mm:ss') : null,
            };
            if (payload.receiptImage && !payload.receiptImage.startsWith('data:image/')) {
                delete payload.receiptImage;
            }
            return payload;
        },
        transformForUpdate(transaction) {
            // Tax values come from backend preview response (already in transaction data)
            const payload = {
                operation_type: 'update',
                member_id: transaction.member_id,
                merchant_id: transaction.merchant_id,
                point: transaction.point,
                detail: transaction.detail.map(item => ({
                    transaction_id: transaction.id,
                    code: item.code,
                    name: item.name,
                    qty: item.qty,
                    subtotal: typeof item.subtotal === 'number' ? item.subtotal : parseInt(item.subtotal) || 0,
                    product_id: item.product_id
                })),
                tax_charge: this.transaction.tax_charge,
                service_charge: this.transaction.service_charge,
                tax_value: this.transaction.tax_value,
                service_value: this.transaction.service_value,
                before_tax_service: this.transaction.before_tax_service,
                after_tax_service: this.transaction.after_tax_service,
                calculate_tax_service: this.includeTaxAndService ? 1 : 0,
                before_after_tax_service: this.backendPointBase === 'after_tax' ? 1 : 0,
                point_estimated: this.pointEstimate,
                voucher_discount: this.transaction.voucher_discount || 0,
                amount_after_voucher: (this.transaction.voucher_discount && this.transaction.voucher_discount > 0)
                    ? Math.max(0, this.calculateTotal() - (this.transaction.voucher_discount || 0))
                    : null,
                // always send note key so backend $request->exists('note') handles nulling correctly
                note: this.transaction.note?.trim() || null,
            };
            if (transaction.receiptImage) payload.receiptImage = transaction.receiptImage;
            return payload;
        },
        closePendingTransactionWarning: function() {
            this.isPendingTransactionWarningVisible = false;
        },
        change: function(){
            if (!this.transaction.member_id) {
                this.error = "Please select a member first";
                this.isPendingTransactionWarningVisible = true;
                this.actionText = "Select Member";
                this.actionHandler = () => {
                    this.isPendingTransactionWarningVisible = false;
                    this.$nextTick(() => {
                        const selectMemberButton = document.getElementById("select-member-button");
                        if (selectMemberButton) selectMemberButton.click();
                    });
                };
                return;
            }
            if (!this.transaction.detail || this.transaction.detail.length === 0) {
                this.error = "Please add at least one product to the transaction";
                this.isPendingTransactionWarningVisible = true;
                this.actionText = "Add Product";
                this.actionHandler = () => this.showModal('addProduct');
                return;
            }
            if (!this.transaction.note || !this.transaction.note.trim()) {
                this.validation = { note: ['Note is required.'] };
                return;
            }
            const MAX_VALUE = 900000000;
            for (let i = 0; i < this.transaction.detail.length; i++) {
                const detail = this.transaction.detail[i];
                if (detail.subtotal > MAX_VALUE) {
                    this.showToast('error', 'Transaction Value Exceeded', `Item #${i + 1} exceeds maximum value (Rp 900.000.000).`);
                    return;
                }
            }
            const transaction = this.cleanTransaction(this.transaction);
            const currentRouteName = this.currentRouteName;
            const isUpdate = currentRouteName !== 'TransactionForm';
            this.transaction.isLoading = true;
            this.validation = [];
            this.validateReceipt();
            if (this.validation.length > 0) {
                this.transaction.isLoading = false;
                return;
            }
            const payload = isUpdate ? this.transformForUpdate(transaction) : this.transformForCreate(transaction);
            const actionType = isUpdate ? 'Transaction/update' : 'Transaction/create';
            const successMessage = isUpdate ? 'Successfully updated the transaction!' : 'Successfully created the transaction!';
            store.dispatch(actionType, { data: payload })
                .then(() => {
                    this.$router.push({
                        name: 'Transaction',
                        query: { toastType: 'success', toastTitle: 'Success', toastMessage: successMessage }
                    });
                })
                .catch((err) => {
                    const errorString = JSON.stringify(err);
                    if (errorString.includes('Numeric value out of range') || errorString.includes('Out of range value')) {
                        this.showToast('error', 'Transaction Value Exceeded', 'Transaction value exceeds maximum limit.');
                        return;
                    }

                    // Handle specific expiration error from backend
                    if (err?.message === 'TRANSACTION.UPDATE_FAILED.ALREADY_EXPIRED' || err?.response?.data?.message === 'TRANSACTION.UPDATE_FAILED.ALREADY_EXPIRED') {
                        this.error = "Transactions that already have an expired record cannot be changed anymore.";
                        this.isPendingTransactionWarningVisible = true;
                        this.actionText = "OK";
                        this.actionHandler = this.closePendingTransactionWarning;
                    } else if (err.error) {
                        this.error = err.error;
                        this.isPendingTransactionWarningVisible = true;
                        this.actionText = "OK";
                        this.actionHandler = this.closePendingTransactionWarning;
                    } else if (err.response?.data?.error) {
                        try {
                            const parsedError = JSON.parse(err.response.data.error);
                            if (typeof parsedError === 'object' && parsedError !== null) {
                                this.validation = parsedError;
                            } else {
                                this.error = err.response.data.error;
                                this.isPendingTransactionWarningVisible = true;
                                this.actionText = "OK";
                                this.actionHandler = this.closePendingTransactionWarning;
                            }
                        } catch (parseError) {
                            this.error = err.response.data.error;
                            this.isPendingTransactionWarningVisible = true;
                            this.actionText = "OK";
                            this.actionHandler = this.closePendingTransactionWarning;
                        }
                    } else if (err.response?.data?.message) {
                        this.error = err.response.data.message;
                        this.isPendingTransactionWarningVisible = true;
                        this.actionText = "OK";
                        this.actionHandler = this.closePendingTransactionWarning;
                    } else {
                        this.error = "Something went wrong";
                        this.isPendingTransactionWarningVisible = true;
                        this.actionText = "OK";
                        this.actionHandler = this.closePendingTransactionWarning;
                    }
                })
                .finally(() => {
                    this.transaction.isLoading = false;
                });
        },
        cancel: function(){
            this.$router.push({ name: 'Transaction' });
        },
        getInitials(member) {
            if (!member || !member.first_name) return 'M';
            let initials = member.first_name.charAt(0);
            if (member.last_name) {
                initials += member.last_name.charAt(0);
            }
            return initials.toUpperCase();
        },
    }
}
</script>

<style scoped>
/* Reset & Base Styles */
.container-fluid {
    padding: 24px;
    background-color: #f8fafc;
    min-height: 100vh;
}

/* Breadcrumb Styling */
.breadcrumb-wrapper {
    margin-bottom: 24px;
}

/* Card Styling */
.card {
    border: none;
    border-radius: 12px;
    box-shadow: 0 1px 3px rgba(0,0,0,0.05);
    background: #fff;
    overflow: hidden;
}

.transaction-header {
    background: #fff;
    padding: 24px;
    border-bottom: 1px solid #f1f5f9;
}

.header-title h5 {
    font-weight: 700;
    color: #1e293b;
    font-size: 1.15rem;
}

/* Button Custom Styles */
.btn-custom {
    padding: 8px 16px;
    border-radius: 8px;
    font-weight: 600;
    font-size: 13px;
    transition: all 0.2s ease;
    border: 1px solid transparent;
}

.btn-custom-primary {
    background-color: #f87171;
    color: #fff;
}

.btn-custom-primary:hover:not(:disabled) {
    background-color: #ef4444;
    transform: translateY(-1px);
}

.btn-custom-outline {
    background-color: #fff;
    border-color: #e2e8f0;
    color: #64748b;
}

.btn-custom-outline:hover:not(:disabled) {
    border-color: #cbd5e1;
    background-color: #f8fafc;
    color: #475569;
}

.btn-custom-success {
    background-color: #22c55e;
    color: #fff;
    padding: 12px;
}

.btn-custom-danger {
    background-color: #fff;
    border-color: #fee2e2;
    color: #ef4444;
    padding: 12px;
}

/* Member Section */
.form-label-custom {
    font-size: 12px;
    font-weight: 700;
    text-transform: uppercase;
    color: #64748b;
    margin-bottom: 10px;
    display: block;
}

.member-avatar-preview {
    width: 44px;
    height: 44px;
    border-radius: 50%;
    overflow: hidden;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    font-weight: 700;
    font-size: 16px;
}

.member-avatar-preview img {
    width: 100%;
    height: 100%;
    object-fit: cover;
}

/* Table Styling */
.transaction-table-wrapper {
    margin-top: 10px;
}

.transaction-table {
    margin-bottom: 0;
}

/* Clean Slate Custom Table Styles */
.transaction-table-scroll-custom {
    overflow-x: auto;
    background-color: #ffffff;
    border: 1px solid #f1f5f9;
    border-radius: 12px;
}

.premium-custom-table {
    width: 100%;
    border-collapse: collapse;
    background-color: #ffffff;
}

.premium-custom-table thead th {
    padding: 16px 12px;
    background-color: #ffffff;
    font-size: 11px;
    font-weight: 800;
    color: #64748b;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    border-bottom: 2px solid #f8fafc;
    text-align: left;
}

.premium-custom-table tbody td {
    padding: 16px 12px;
    background-color: #ffffff;
    border-bottom: 1px solid #f1f5f9;
    font-size: 14px;
    color: #475569;
    vertical-align: middle;
}

/* Information Badges (Inline) */
.status-badge {
    padding: 3px 6px;
    border-radius: 4px;
    font-size: 9.5px;
    font-weight: 700;
    text-transform: uppercase;
    display: inline-block;
    border: 1px solid transparent;
}

.status-badge.active {
    background-color: #f0f9ff;
    color: #0c4a6e;
    border: 1px solid #bae6fd;
}

.status-badge.expired {
    background-color: #fff1f2;
    color: #9f1239;
    border: 1px solid #fecdd3;
}

.source-badge {
    padding: 3px 6px;
    border-radius: 4px;
    font-size: 9.5px;
    font-weight: 700;
    text-transform: uppercase;
    display: inline-block;
    border: 1px solid transparent;
}

.source-badge.internal {
    background-color: #f0fdf4;
    color: #166534;
    border: 1px solid #bbf7d0;
}

.source-badge.external-nett {
    background-color: #f8fafc;
    color: #475569;
    border: 1px solid #e2e8f0;
}

/* Member Avatar Sync */
.member-avatar {
    width: 32px;
    height: 32px;
    border-radius: 50%;
    background: #e2e8f0;
    color: #475569;
    font-weight: 700;
    font-size: 13px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    overflow: hidden;
}

.member-avatar img {
    width: 100%;
    height: 100%;
    object-fit: cover;
}

.form-label-premium {
    font-size: 11px;
    font-weight: 800;
    color: #94a3b8;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    margin-bottom: 4px;
    display: block;
}

/* Code Copy Styling */
.code-copy-wrapper {
    display: flex;
    align-items: center;
    gap: 8px;
}

.btn-copy-mini {
    border: none;
    background: transparent;
    color: #cbd5e1;
    cursor: pointer;
    transition: color 0.2s;
    font-size: 12px;
}

.btn-copy-mini:hover { color: #3b82f6; }

/* Premium Table Sync with Main List (Flat & Clean) */
.transaction-table-scroll-custom {
    background: #fff;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
}

.premium-custom-table thead th {
    padding: 12px 12px;
    background-color: #f8fafc !important;
    font-size: 11px;
    font-weight: 700;
    color: #475569;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    border-bottom: 2px solid #f1f5f9;
}

.premium-custom-table tbody td {
    padding: 14px 12px;
    background-color: #ffffff;
    border-bottom: 1px solid #f1f5f9;
    vertical-align: middle;
}

/* Action Buttons Sync (Flat Square Style) */
.action-btn-sync {
    display: flex;
    justify-content: center;
    gap: 6px;
}

.btn-sync-action {
    width: 32px;
    height: 32px;
    border-radius: 6px;
    border: none;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    font-size: 13px;
    transition: background-color 0.2s;
}

.btn-sync-edit {
    background-color: #eff6ff;
    color: #3b82f6;
}

.btn-sync-edit:hover {
    background-color: #dbeafe;
}

.btn-sync-delete {
    background-color: #fee2e2;
    color: #ef4444;
}

.btn-sync-delete:hover {
    background-color: #fecaca;
}

/* Data Column Tuning */
.p-custom-code-short {
    background-color: #f8fafc;
    color: #64748b;
    padding: 2px 6px;
    border-radius: 4px;
    font-size: 11px;
    max-width: 90px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    display: inline-block;
}

.badge-disc-custom {
    background: #fef2f2;
    color: #ef4444;
    padding: 4px 10px;
    border-radius: 6px;
    font-weight: 700;
    font-size: 11px;
    letter-spacing: -0.01em;
}

.text-dark-soft { color: #334155; font-weight: 600; }
.text-muted-xs { color: #94a3b8; font-size: 12px; }

/* Table Footer Sync */
.footer-total-row-custom td {
    padding: 20px 12px;
    border-top: 2px solid #f8fafc;
}

.footer-label-custom {
    font-size: 11px;
    font-weight: 800;
    color: #94a3b8;
    text-transform: uppercase;
    letter-spacing: 0.1em;
}

.footer-value-custom {
    font-size: 20px;
    font-weight: 800;
    color: #f87171;
    letter-spacing: -0.02em;
}

/* Combined Summary Sidebar */
.summary-card {
    border: none;
    box-shadow: 0 4px 15px rgba(0,0,0,0.05);
    z-index: 0;
}

.summary-header {
    background: #fff;
    padding: 24px;
    border-bottom: 1px solid #f1f5f9;
}

.summary-header h6 {
    font-weight: 700;
    font-size: 1.05rem;
    color: #1e293b;
    text-transform: uppercase;
    letter-spacing: 0.05em;
}

.summary-section {
    border-bottom: 1px solid #f1f5f9;
}

.summary-section:last-child {
    border-bottom: none;
}

.summary-row, .summary-row-sm, .summary-row-grand {
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.summary-label { color: #64748b; font-size: 14px; font-weight: 500; }
.summary-value { color: #1e293b; font-weight: 700; font-size: 15px; }

.label-sm { color: #94a3b8; font-size: 12px; }
.value-sm { color: #64748b; font-size: 12px; font-weight: 600; }

.label-grand { color: #1e293b; font-weight: 700; font-size: 14px; }
.value-grand { color: #1e293b; font-weight: 700; font-size: 20px; }

/* Point Calculation Section Redesign */
.point-generation-card {
    background: linear-gradient(145deg, #fffafa, #ffffff);
    padding: 24px;
    border-bottom: 1px solid #f1f5f9;
}

.point-heading {
    font-size: 13px;
    font-weight: 800;
    color: #334155;
    text-transform: uppercase;
    letter-spacing: 0.05em;
}

.badge-point-base {
    padding: 4px 10px;
    border-radius: 6px;
    font-size: 10px;
    font-weight: 800;
    text-transform: uppercase;
    background: #fef2f2;
    color: #ef4444;
    letter-spacing: 0.05em;
}

.point-notice-soft {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    font-size: 12px;
    color: #64748b;
    line-height: 1.4;
    padding: 10px 12px;
    background: rgba(255, 255, 255, 0.8);
    border-radius: 8px;
    border: 1px solid #f1f5f9;
}

.point-notice-soft i {
    margin-top: 3px;
    color: #94a3b8;
}

.point-score-board {
    display: flex;
    align-items: center;
    gap: 16px;
}

.point-icon-large {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 48px;
    height: 48px;
    border-radius: 12px;
    background: var(--secondary-color);
    color: var(--primary-color);
    font-size: 20px;
}

.point-score-content {
    display: flex;
    align-items: baseline;
    gap: 8px;
}

.point-count-large {
    font-size: 36px;
    font-weight: 800;
    color: var(--primary-color);
    line-height: 1;
    letter-spacing: -0.02em;
}

.point-unit-large {
    font-size: 14px;
    font-weight: 800;
    color: #94a3b8;
    text-transform: uppercase;
    letter-spacing: 0.1em;
}

/* Main Actions */
.btn-main-action {
    background-color: var(--primary-color);
    color: white;
    padding: 12px;
    border-radius: 10px;
    font-weight: 700;
    font-size: 14px;
    border: none;
    transition: all 0.2s ease;
}

.btn-main-action:hover:not(:disabled) {
    filter: brightness(0.9);
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(248, 113, 113, 0.2);
}

.btn-secondary-action {
    background-color: #fff;
    color: #94a3b8;
    padding: 12px;
    border-radius: 10px;
    font-weight: 600;
    border: 2px solid #f1f5f9;
}

.btn-secondary-action:hover {
    background-color: #f1f5f9;
    color: #64748b;
}

/* Toggles */
.toggle-switch-sm {
    position: relative;
    display: inline-block;
    width: 36px;
    height: 20px;
}

.toggle-switch-sm input { opacity: 0; width: 0; height: 0; }

.toggle-slider-sm {
    position: absolute;
    cursor: pointer;
    top: 0; left: 0; right: 0; bottom: 0;
    background-color: #e2e8f0;
    transition: .4s;
    border-radius: 20px;
}

.toggle-slider-sm:before {
    position: absolute;
    content: "";
    height: 14px; width: 14px;
    left: 3px; bottom: 3px;
    background-color: white;
    transition: .4s;
    border-radius: 50%;
}

input:checked + .toggle-slider-sm { background-color: var(--primary-color); }
input:checked + .toggle-slider-sm:before { transform: translateX(16px); }

.toggle-text {
    font-size: 11px;
    font-weight: 700;
    color: #cbd5e1;
}

.toggle-text.active { color: #64748b; }

.btn-action-minimal {
    width: 34px;
    height: 34px;
    border-radius: 8px;
    border: none;
    background: #f8fafc;
    color: #64748b;
    transition: all 0.2s ease;
}

.btn-edit:hover { background: #eff6ff; color: #3b82f6; }
.btn-delete:hover { background: #fef2f2; color: #ef4444; }

.text-primary { color: #f87171 !important; }
</style>

<style scoped>
.toast-container {
    position: fixed;
    top: 20px;
    right: 20px;
    z-index: 9999;
    display: flex;
    flex-direction: column;
    gap: 10px;
}

/* Card Styling */
.transaction-card,
.calculation-card,
.point-card {
    border: 1px solid #e9ecef;
    border-radius: 8px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.card-header {
    background-color: transparent;
    border-bottom: 1px solid #f1f5f9;
    padding: 1.25rem;
}

.card-body {
    padding: 1.25rem;
}

/* Transaction Source Badge */
.transaction-source-badge {
    background-color: #f8f9fa;
    border: 1px solid #e9ecef;
    border-radius: 6px;
    padding: 12px 16px;
}

.source-info {
    display: flex;
    align-items: center;
    gap: 12px;
    flex-wrap: wrap;
}

.source-label {
    font-size: 13px;
    color: #6c757d;
    font-weight: 500;
}

.source-value {
    font-size: 13px;
    font-weight: 600;
    padding: 4px 10px;
    border-radius: 4px;
}

.source-value.external {
    background-color: #fff3cd;
    color: #856404;
}

.source-value.internal {
    background-color: #d4edda;
    color: #155724;
}

.source-type {
    font-size: 10px;
    font-weight: 600;
    padding: 4px 8px;
    border-radius: 6px;
    text-transform: uppercase;
}

.source-type.nett {
    background-color: #ebf5ff;
    color: #004e9a;
}

.source-type.taxservice {
    background-color: #fff1f2;
    color: #9f1239;
}

/* Table Styling */
.transaction-table-wrapper {
    border: 1px solid #f1f5f9;
    border-radius: 0;
    overflow-x: auto;
    background: #fff;
}

.transaction-table {
    margin-bottom: 0;
    width: 100%;
    border-collapse: separate;
    border-spacing: 0;
    min-width: 1000px; /* Force scroll on small screens */
}

.transaction-table thead {
    display: table-header-group;
    height: auto;
}

.transaction-table thead th,
.transaction-table tfoot th {
    background-color: var(--primary-color);
    color: #ffffff;
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    padding: 14px 16px;
    border: none !important;
    box-shadow: none !important;
    height: auto;
    line-height: 1.5;
    white-space: nowrap;
}

.transaction-table tfoot th {
    font-size: 14px;
}


.transaction-table tbody td {
    padding: 14px 16px;
    vertical-align: middle;
    color: #334155;
    font-size: 13px;
    border-bottom: 1px solid #f1f5f9;
    white-space: nowrap;
}

.transaction-row:hover {
    background-color: #f8fafc;
}

.product-code {
    background-color: #f1f5f9;
    color: #475569;
    padding: 4px 8px;
    border-radius: 6px;
    font-size: 11px;
    font-family: inherit;
    font-weight: 600;
}

.product-name {
    font-weight: 600;
    color: #1e293b;
}

.qty-cell {
    font-weight: 600;
}

.price-cell, .subtotal-cell {
    font-family: 'Inter', sans-serif;
    letter-spacing: -0.2px;
}

/* Calculation Summary */
.calc-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 8px 0;
    border-bottom: 1px solid #f8f9fa;
}

.calc-row:last-child {
    border-bottom: none;
}

.calc-sub-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 4px 0 4px 20px;
    font-size: 13px;
    color: #6c757d;
}

.grandtotal-row {
    border-top: 2px solid #dee2e6;
    margin-top: 8px;
    padding-top: 12px;
    font-weight: 600;
    font-size: 15px;
}

.calc-label {
    font-size: 14px;
    color: #495057;
}

.calc-value {
    font-size: 14px;
    font-weight: 700;
    color: #1e293b;
}

.calc-sub-label {
    font-size: 13px;
    color: #6c757d;
}

.calc-sub-value {
    font-size: 13px;
    color: #6c757d;
}

/* Tax Service Toggle */
.tax-service-toggle {
    margin-top: 16px;
    padding: 16px;
    background-color: #f8f9fa;
    border-radius: 6px;
}

.toggle-section {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
}

.toggle-label {
    font-size: 13px;
    font-weight: 500;
    color: #6c757d;
}

.toggle-wrapper {
    display: flex;
    align-items: center;
    gap: 12px;
}

.toggle-option {
    font-size: 13px;
    font-weight: 500;
    color: #adb5bd;
    transition: color 0.2s ease;
}

.toggle-option.active {
    color: #212529;
    font-weight: 600;
}

.toggle-switch {
    position: relative;
    display: inline-block;
    width: 44px;
    height: 24px;
}

.toggle-switch input {
    opacity: 0;
    width: 0;
    height: 0;
}

.toggle-slider {
    position: absolute;
    cursor: pointer;
    inset: 0;
    background-color: #dee2e6;
    transition: 0.3s;
    border-radius: 24px;
}

.toggle-slider:before {
    position: absolute;
    content: "";
    height: 18px;
    width: 18px;
    left: 3px;
    bottom: 3px;
    background-color: white;
    transition: 0.3s;
    border-radius: 50%;
}

input:checked + .toggle-slider {
    background-color: #198754;
}

input:checked + .toggle-slider:before {
    transform: translateX(20px);
}

input:disabled + .toggle-slider {
    opacity: 0.5;
    cursor: not-allowed;
}

/* Point Calculation */
.point-info-list {
    display: flex;
    flex-direction: column;
    gap: 12px;
}

.point-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.point-label {
    font-size: 13px;
    color: #64748b;
    font-weight: 500;
}

.point-value {
    font-size: 13px;
    font-weight: 600;
}

.point-value.before_tax {
    color: #166534;
}

.point-value.after_tax {
    color: #854d0e;
}

.point-amount {
    font-weight: 600;
    color: #1e293b;
    font-size: 14px;
}

.point-result-row {
    padding-top: 12px;
    border-top: 1px dashed #e2e8f0;
    margin-top: 4px;
}

.point-result-text {
    font-size: 18px;
    font-weight: 700;
    color: var(--primary-color);
}

.point-result-text.loading {
    opacity: 0.5;
}

.point-note {
    padding: 10px 14px;
    background-color: #f8fafc;
    border-radius: 8px;
    font-size: 12px;
    color: #64748b;
    border: 1px solid #f1f5f9;
}

/* Action Buttons Container */
.action-buttons-container {
    position: sticky;
    bottom: 20px;
    background: white;
    padding: 20px;
    border-radius: 12px;
    border: 1px solid #f1f5f9;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
    z-index: 100;
}

.action-buttons {
    position: relative;
}

/* Custom Buttons Styling */
.btn-custom {
    font-weight: 600;
    font-size: 13px;
    height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 8px;
    padding: 0 20px;
    transition: all 0.2s;
    border: none;
    cursor: pointer;
    box-shadow: none !important;
}

.btn-custom-primary {
    background-color: var(--primary-color) !important;
    color: white !important;
}

.btn-custom-primary:hover {
    filter: brightness(0.9);
}

.btn-custom-outline {
    background-color: #fff !important;
    border: 1px solid #e2e8f0 !important;
    color: #475569 !important;
}

.btn-custom-outline:hover {
    background-color: #f1f5f9;
    border-color: #cbd5e1 !important;
}

.btn-custom-success {
    background-color: #22c55e !important;
    color: white !important;
}

.btn-custom-success:hover {
    background-color: #16a34a !important;
}

.btn-custom-danger {
    background-color: #fff !important;
    border: 1px solid #fee2e2 !important;
    color: #ef4444 !important;
}

.btn-custom-danger:hover {
    background-color: #fef2f2;
    border-color: #fecaca !important;
}

/* Header Actions */
.header-actions {
    display: flex;
    gap: 8px;
}

/* Breadcrumb */
.breadcrumb-wrapper {
    margin-top: 1.5rem;
    margin-bottom: 0.5rem;
}

/* Responsive */
@media (max-width: 991px) {
    .header-actions {
        flex-wrap: wrap;
        gap: 6px;
    }

    .btn-custom {
        font-size: 12px;
        height: 36px;
        padding: 0 12px;
    }
}

@media (max-width: 768px) {
    .container-fluid {
        padding: 0 15px;
    }

    .source-info {
        flex-direction: column;
        align-items: flex-start;
        gap: 8px;
    }

    .toggle-section {
        flex-direction: column;
        align-items: flex-start;
        gap: 12px;
    }

    .header-actions {
        width: 100%;
        justify-content: flex-start;
        margin-top: 12px;
    }

    .point-result-value {
        font-size: 24px;
    }
}

@media (max-width: 576px) {
    .header-actions .btn {
        font-size: 11px;
        padding: 4px 8px;
    }

    .header-actions .btn i {
        display: none;
    }
}
/* Action Buttons in Table */
.action-buttons-group {
    display: flex;
    gap: 6px;
    justify-content: center;
}

.btn-edit-item, .btn-tax-item, .btn-action-delete {
    border-radius: 6px;
    border: none;
    transition: all 0.2s;
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
}

.btn-edit-item {
    background-color: #f1f5f9;
    color: #475569;
}

.btn-edit-item:hover {
    background-color: #e2e8f0;
    color: #1e293b;
}

.btn-action-delete {
    background-color: #f1f5f9;
    color: #475569;
}

/* Badges Layout */
.badges-container {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px;
}

.source-info, .status-info {
    display: flex;
    align-items: center;
    gap: 8px;
}

/* Badge Label Styling (Similar to Source:) */
.badge-label {
    font-size: 13px;
    color: #64748b;
}

/* Base Badge Style */
.badge-value {
    padding: 4px 12px;
    border-radius: 6px;
    font-size: 13px;
    font-weight: 600;
}

/* Source: Membership Apps */
.badge-value.internal {
    background-color: #dcfce7;
    color: #166534;
}

/* Source: External */
.badge-value.external {
    background-color: #f1f5f9;
    color: #475569;
}

/* Status: Active (Consistent with Design) */
.badge-value.active {
    background-color: #e0f2fe;
    color: #0369a1;
    border: 1px solid #bae6fd;
}

/* Status: Expired (Consistent with Design) */
.badge-value.expired {
    background-color: #fff1f2;
    color: #be123c;
    border: 1px solid #fecdd3;
}

/* Badge Type (Gross/Nett) */
.badge-type {
    padding: 2px 8px;
    border-radius: 4px;
    font-size: 11px;
    font-weight: 500;
    text-transform: uppercase;
}

.badge-type.nett {
    background-color: #e2e8f0;
    color: #475569;
}

.badge-type.gross {
    background-color: #fef9c3;
    color: #854d0e;
}

.btn-tax-item {
    background-color: #fefce8;
    color: #854d0e;
}

.btn-tax-item:hover {
    background-color: #fef9c3;
    color: #713f12;
}

/* Error Alert Styles */
.error-alert {
    display: flex;
    gap: 10px;
    background: #fff5f5;
    border: 1px solid #fecaca;
    border-radius: 8px;
    padding: 12px 14px;
    align-items: flex-start;
}

.error-alert-icon {
    color: #ef4444;
    font-size: 15px;
    flex-shrink: 0;
    margin-top: 1px;
}

.error-alert-title {
    font-size: 12px;
    font-weight: 600;
    color: #b91c1c;
    margin: 0 0 2px 0;
}

.error-list {
    margin: 0;
    padding-left: 14px;
    font-size: 12px;
    color: #dc2626;
}

.error-list li {
    margin-bottom: 1px;
}
/* Horizontal Voucher UI Styles */
.voucher-horizontal-container {
    position: relative;
    width: 100%;
    overflow: hidden;
}
.voucher-horizontal-list {
    display: flex;
    overflow-x: auto;
    gap: 16px;
    padding: 16px 4px;
    scroll-behavior: smooth;
    -webkit-overflow-scrolling: touch;
}
.voucher-horizontal-list::-webkit-scrollbar {
    height: 6px;
}
.voucher-horizontal-list::-webkit-scrollbar-track {
    background: #f1f5f9;
    border-radius: 8px;
}
.voucher-horizontal-list::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 8px;
}

.voucher-horiz-card {
    flex: 0 0 auto;
    width: 220px;
    background: #ffffff;
    border: 1px solid #e2e8f0;
    border-radius: 12px;
    padding: 16px;
    cursor: pointer;
    transition: all 0.25s ease;
    box-shadow: 0 2px 5px rgba(0,0,0,0.02);
    position: relative;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
}
.voucher-horiz-card:hover:not(.disabled) {
    border-color: #10b981;
    box-shadow: 0 4px 12px rgba(16, 185, 129, 0.1);
    transform: translateY(-2px);
}
.voucher-horiz-card.selected {
    border-color: #10b981;
    background: #f0fdf4;
}
.voucher-horiz-card.disabled {
    opacity: 0.6;
    cursor: not-allowed;
    background: #f8fafc;
}

.voucher-horiz-header {
    margin-bottom: 12px;
    position: relative;
}
.badge-best-deal {
    position: absolute;
    top: -24px;
    left: -16px;
    background: #10b981;
    color: white;
    font-size: 9px;
    font-weight: 700;
    padding: 2px 10px;
    border-radius: 12px 0 12px 0;
    text-transform: uppercase;
}
.discount-huge {
    font-size: 28px;
    font-weight: 800;
    color: #10b981;
    line-height: 1;
}
.off-text {
    font-size: 11px;
    font-weight: 700;
    color: #64748b;
    vertical-align: super;
}

.voucher-horiz-body {
    flex-grow: 1;
}
.v-title {
    font-weight: 700;
    font-size: 12px;
    color: #1e293b;
    margin-bottom: 6px;
    line-height: 1.3;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
    word-break: break-word;
}
.v-desc {
    font-size: 9px;
    color: #64748b;
    margin-bottom: 2px;
}

.voucher-horiz-footer {
    position: absolute;
    bottom: 16px;
    right: 16px;
}
.uncheck-circle {
    width: 22px;
    height: 22px;
    border-radius: 50%;
    border: 2px solid #cbd5e1;
}
.check-circle {
    width: 22px;
    height: 22px;
    border-radius: 50%;
    background: #10b981;
    color: white;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 11px;
}

.applied-alert {
    background: #f0fdf4;
    border: 1px solid #bbf7d0;
    border-radius: 8px;
    padding: 10px 14px;
}
.fintech-empty-voucher {
    background: #f8fafc;
    border: 1px dashed #cbd5e1;
    border-radius: 12px;
    padding: 24px 16px;
}
.voucher-count-badge {
    position: absolute;
    top: 0;
    right: 0;
    background-color: #ffc107;
    color: #212529;
    padding: 4px 10px;
    border-radius: 0 12px 0 12px;
    font-size: 11px;
    font-weight: 700;
    z-index: 1;
}
</style>
